import { API_BASE, del, get, patch } from '@/lib/api';

export type BugReportMode = 'text' | 'screen' | 'screen_audio';
export type BugReportStatus = 'open' | 'addressed' | 'wont_fix';
export type BugReportCategory = 'bug' | 'ui' | 'feature' | 'question';

export interface BugReportEvent {
  t: number;
  type: string;
  [key: string]: unknown;
}

export interface BugReportSummary {
  id: string;
  status: BugReportStatus;
  category: BugReportCategory;
  mode: BugReportMode;
  description: string;
  pageUrl: string | null;
  reporter: { id: string | null; email: string | null; name: string | null };
  team: { id: string | null; name: string | null };
  hasVideo: boolean;
  hasHtml: boolean;
  durationMs: number | null;
  createdAt: string;
  addressedAt: string | null;
  url: string;
}

export interface BugReport extends BugReportSummary {
  userAgent: string | null;
  adminNotes: string | null;
  updatedAt: string;
  video: { url: string; type: string | null; bytes: number | null } | null;
  htmlUrl: string | null;
  adminUrl: string;
  context: {
    startedAt?: number;
    endedAt?: number;
    url?: string;
    viewport?: { width: number; height: number; dpr: number };
    timezone?: string;
    language?: string;
    theme?: string;
    events?: BugReportEvent[];
    truncated?: boolean;
  } | null;
}

export interface BugReportList {
  items: BugReportSummary[];
  counts: Record<BugReportStatus, number>;
}

export interface NewBugReport {
  description: string;
  mode: BugReportMode;
  category: BugReportCategory;
  pageUrl: string;
  durationMs?: number;
  context: Record<string, unknown>;
  video?: Blob | null;
  html?: string | null;
}

/**
 * Multipart, through XHR rather than fetch, for the one thing fetch still
 * cannot do: report upload progress on a recording that may be tens of MB.
 */
export function submitBugReport(
  report: NewBugReport,
  onProgress?: (fraction: number) => void
): Promise<{ id: string }> {
  const form = new FormData();
  form.set(
    'meta',
    JSON.stringify({
      description: report.description,
      mode: report.mode,
      category: report.category,
      pageUrl: report.pageUrl,
      durationMs: report.durationMs,
      context: report.context
    })
  );
  if (report.video) {
    const ext = report.video.type.includes('mp4') ? 'mp4' : 'webm';
    form.set('video', report.video, `recording.${ext}`);
  }
  if (report.html) form.set('html', new Blob([report.html], { type: 'text/html' }), 'page.html');

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_BASE}/api/bug-reports`);
    xhr.withCredentials = true;
    xhr.responseType = 'json';
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress?.(e.loaded / e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve(xhr.response as { id: string });
      else {
        const body = xhr.response as { error?: string } | null;
        reject(new Error(body?.error ?? `Upload failed (${xhr.status})`));
      }
    };
    xhr.onerror = () => reject(new Error('Network error while sending the report'));
    xhr.send(form);
  });
}

export const fetchBugReports = (
  query: { status: BugReportStatus | 'all'; category?: string },
  signal?: AbortSignal
) => get<BugReportList>('/api/admin/bug-reports', { ...query, limit: 200 }, signal);

export const fetchBugReport = (id: string, signal?: AbortSignal) =>
  get<BugReport>(`/api/admin/bug-reports/${id}`, undefined, signal);

export const updateBugReport = (
  id: string,
  body: { status?: BugReportStatus; category?: BugReportCategory; adminNotes?: string | null }
) => patch<BugReport>(`/api/admin/bug-reports/${id}`, body);

export const deleteBugReport = (id: string) => del<void>(`/api/admin/bug-reports/${id}`);

export async function fetchBugReportMarkdown(id: string): Promise<string> {
  const res = await fetch(`${API_BASE}/api/admin/bug-reports/${id}?format=md`, { credentials: 'include' });
  if (!res.ok) throw new Error(`Request failed (${res.status})`);
  return res.text();
}

export const bugReportMediaUrl = (id: string, kind: 'video' | 'html') =>
  `${API_BASE}/api/admin/bug-reports/${id}/${kind}`;
